import { Edit as EditIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactForm } from '@sophocles/components/contacts';
import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

import ContactModificationConfirmDialog from '../ContactModificationConfirmDialog/ContactModificationConfirmDialog';
import { useAddressFetch } from '../hooks/useAddressFetch';
import { useContactFetch } from '../hooks/useContactFetch';
import { useContactMatcher } from '../hooks/useContactMatcher';

/**
 * AddressBookDialog - Modal for selecting contacts from address book
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {function} props.onSelect - Selection handler: (contact) => void
 * @param {number} props.customerId - Customer ID
 * @param {number} props.siteGroupId - Site Group ID (optional)
 * @param {Object} props.customer - Customer object (for is_billboard check)
 * @param {Object} props.siteGroup - Site Group object
 * @param {Object} props.value - Currently selected contact
 * @param {function} props.onEdit - Edit handler: (contact) => void
 * @param {function} props.onAddNew - Add new contact handler
 * @param {string} props.mode - Selection mode:
 *   - 'contact': Full contact selection (default) - shows contacts, requires address for selection
 *   - 'addressOnly': Only show customer/site group default addresses
 *   - 'quick': Quick contact selection - only requires name and email
 * @param {boolean} props.showDefaultAddresses - If true, show default addresses section (default: false)
 * @param {Object} props.initialEditingContact - Contact to edit when dialog opens
 * @param {boolean} props.savedContactsOnly - If true, hide "Enter Details Manually" button (default: false)
 * @param {string[]} props.requiredFields - Array of required fields for filtering and form validation
 *   Valid values: 'email', 'name', 'address', 'phone'
 *   - 'email': Only show contacts with email, make email required in form
 *   - 'name': Only show contacts with name or business_name
 *   - 'address': Only show contacts with complete address, make address required in form
 *   - 'phone': Make phone required in form (for future use)
 * @param {string} props.addressType - Filter for address type: 'shipping', 'billing', or 'all' (default: 'all')
 * @param {function} props.onAutoSelect - Callback when auto-selection occurs in addressOnly mode: (contact) => void
 */
const AddressBookDialog = ({
  open,
  onClose,
  onSelect,
  customerId,
  siteGroupId,
  customer,
  siteGroup,
  value,
  onEdit,
  onAddNew,
  mode = 'contact',
  showDefaultAddresses = false,
  initialEditingContact = null,
  savedContactsOnly = false,
  requiredFields = [],
  addressType = 'all',
  onAutoSelect = null,
  primaryContext = 'none', // 'all' | 'rma' | 'sales' | 'none' - controls which primary options show in ContactForm
}) => {
  const { t } = useTranslation();

  // Search state with debouncing
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Derive settings from mode
  const isAddressOnlyMode = mode === 'addressOnly';
  const isQuickMode = mode === 'quick';

  // In addressOnly mode, we don't fetch contacts - only default addresses
  // In contact mode, require complete address unless quick mode
  const requireCompleteAddress = !isQuickMode && requiredFields.includes('address');

  // Fetch all contacts (both site group and customer level) with server-side search
  // Backend returns both when customerId AND siteGroupId are provided
  // requiredFields array filters contacts (e.g., ['email', 'name'] only shows contacts with both)
  // Skip contact fetch in addressOnly mode
  const {
    contacts: fetchedContacts,
    loading: contactsLoading,
    error: contactsError,
    refetch: refetchContacts,
  } = useContactFetch({
    customerId: isAddressOnlyMode ? null : customerId,
    siteGroupId: isAddressOnlyMode ? null : siteGroupId,
    completeAddressOnly: requireCompleteAddress,
    limit: 20, // Increased limit since we're fetching both levels in one call
    search: debouncedSearch,
    requiredFields,
  });

  // Separate contacts by level (site_group vs customer)
  // Site group contacts have site_group set, customer contacts have site_group=null
  // Sort each group to put primary contacts at the top
  const sortContactsByPrimary = contacts => {
    return [...contacts].sort((a, b) => {
      // Calculate priority score (lower = higher priority)
      const getPriority = contact => {
        // Count how many primary designations this contact has
        const primaryCount = [
          contact.is_primary_rma_billing,
          contact.is_primary_rma_shipping,
          contact.is_primary_sales_billing,
          contact.is_primary_sales_shipping,
        ].filter(Boolean).length;

        // More primaries = higher priority (lower score)
        if (primaryCount >= 3) return 0;
        if (primaryCount === 2) return 1;
        if (primaryCount === 1) return 2;
        return 3;
      };
      return getPriority(a) - getPriority(b);
    });
  };

  // Client-side filter for contacts with complete address (fallback if backend filter misses some)
  const hasAddressRequirement = requiredFields.includes('address');
  const filterByAddress = contacts => {
    if (!hasAddressRequirement) return contacts;
    return contacts.filter(contact => {
      // Check for complete address: address, city, state, and zipcode
      const hasAddress = contact.address && contact.address.trim() && !contact.address.includes('None');
      const hasCity = contact.city && contact.city.trim() && !contact.city.includes('None');
      const hasState = contact.state && contact.state.trim() && !contact.state.includes('None');
      const hasZip = contact.zipcode && contact.zipcode.trim() && !contact.zipcode.includes('None');
      return hasAddress && hasCity && hasState && hasZip;
    });
  };

  // Client-side filter for contacts with email or phone (fallback if backend filter misses some)
  const hasEmailOrPhoneRequirement = requiredFields.includes('emailOrPhone');
  const filterByEmailOrPhone = contacts => {
    if (!hasEmailOrPhoneRequirement) return contacts;
    return contacts.filter(contact => {
      const hasEmail = contact.email && contact.email.trim();
      const hasPhone = contact.phone1 && contact.phone1.trim();
      return hasEmail || hasPhone;
    });
  };

  // Client-side filter for contacts with email (fallback if backend filter misses some)
  const hasEmailRequirement = requiredFields.includes('email');
  const filterByEmail = contacts => {
    if (!hasEmailRequirement) return contacts;
    return contacts.filter(contact => {
      return contact.email && contact.email.trim();
    });
  };

  // Apply all filters
  const applyFilters = contacts => {
    return filterByEmail(filterByEmailOrPhone(filterByAddress(contacts)));
  };

  const siteGroupContacts = sortContactsByPrimary(
    applyFilters(fetchedContacts.filter(contact => contact.site_group != null)),
  );
  const customerContacts = sortContactsByPrimary(
    applyFilters(fetchedContacts.filter(contact => contact.site_group == null)),
  );

  // Fetch default addresses (if showDefaultAddresses is true OR addressOnly mode)
  const shouldFetchAddresses = showDefaultAddresses || isAddressOnlyMode;
  // Extract names from customer/siteGroup objects (may be Immutable.js or plain objects)
  const customerDisplayName = customer?.get ? customer.get('name', 'Customer') : customer?.name || 'Customer';
  const siteGroupDisplayName = siteGroup?.get ? siteGroup.get('name', 'Site Group') : siteGroup?.name || 'Site Group';

  const {
    addresses: defaultAddresses,
    loading: addressesLoading,
    error: addressesError,
  } = useAddressFetch({
    customerId: shouldFetchAddresses ? customerId : null,
    siteGroupId: shouldFetchAddresses ? siteGroupId : null,
    primaryOnly: true, // Only fetch primary (default) addresses
    addressType, // Filter by shipping/billing/all
    customerName: customerDisplayName,
    siteGroupName: siteGroupDisplayName,
  });

  const [editingContact, setEditingContact] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [modifiedContact, setModifiedContact] = useState(null);
  const [showModificationConfirm, setShowModificationConfirm] = useState(false);
  const [hasAutoSelected, setHasAutoSelected] = useState(false); // Track if auto-selection has occurred

  const loading = contactsLoading || addressesLoading;
  const error = contactsError || addressesError;

  // Apply same filters to default addresses (email requirement, etc.)
  const filteredAddresses = applyFilters(defaultAddresses);

  // Auto-select logic for addressOnly mode
  // Prioritize site group address, fallback to customer address
  useEffect(() => {
    // Only auto-select in addressOnly mode when:
    // 1. Dialog is NOT open (auto-select happens before dialog opens)
    // 2. Addresses are loaded and not loading
    // 3. No value is currently selected
    // 4. Auto-selection hasn't occurred yet for this instance
    // 5. onAutoSelect callback is provided
    if (
      isAddressOnlyMode &&
      !open &&
      !addressesLoading &&
      filteredAddresses.length > 0 &&
      !value &&
      !hasAutoSelected &&
      onAutoSelect
    ) {
      // Separate addresses by scope (using filteredAddresses which respects requiredFields)
      const siteGroupAddresses = filteredAddresses.filter(addr => addr._addressScope === 'Site Group');
      const customerAddresses = filteredAddresses.filter(addr => addr._addressScope === 'Customer');

      // Prioritize site group address, fallback to customer
      const bestAddress = siteGroupAddresses[0] || customerAddresses[0];

      if (bestAddress) {
        setHasAutoSelected(true);
        onAutoSelect(bestAddress);
      }
    }
  }, [isAddressOnlyMode, open, addressesLoading, filteredAddresses, value, hasAutoSelected, onAutoSelect]);

  // Reset hasAutoSelected when customerId or siteGroupId changes
  useEffect(() => {
    setHasAutoSelected(false);
  }, [customerId, siteGroupId]);

  // Find matching contact by email if value is provided
  // This handles both real contacts (with id) and synthetic contacts (id: null, _isSynthetic: true)
  // Include default addresses in allContacts
  const allContacts = [...filteredAddresses, ...siteGroupContacts, ...customerContacts];

  // Use comprehensive matching that includes address fields
  const inlineFields =
    value && !value.id
      ? {
          name: value.name,
          email: value.email,
          phone: value.phone1,
          address: value.address,
          address_2: value.address_2, // Include address_2 for apartment/suite matching
          city: value.city,
          state: value.state,
          zip: value.zipcode,
          country: value.country,
          business_name: value.business_name,
        }
      : null;

  const matchedByFields = useContactMatcher(inlineFields, allContacts);

  const matchedContact = value
    ? value.id && !value._isManualEntry
      ? allContacts.find(c => c.id === value.id) // Match by ID if it's a saved contact or Address record
      : matchedByFields // Use comprehensive matching for manual/inline entries
    : null;

  const [selectedContactId, setSelectedContactId] = useState(matchedContact?.id || null);

  // Update selectedContactId when dialog opens with new value
  React.useEffect(() => {
    if (open && value) {
      // Use the same comprehensive matching logic
      if (value.id && !value._isManualEntry) {
        // Saved contact or Address record - match by ID
        const contact = allContacts.find(c => c.id === value.id);
        setSelectedContactId(contact?.id || null);
      } else {
        // Manual/inline entry - use comprehensive matching
        setSelectedContactId(matchedByFields?.id || null);
      }
    }
  }, [open, value?.id, value?.email, value?.address, value?.city, allContacts.length, matchedByFields?.id]);

  // Auto-enter edit mode when initialEditingContact is provided
  React.useEffect(() => {
    if (open && initialEditingContact) {
      setEditingContact(initialEditingContact);
      setAddingNew(false);
    } else if (!open) {
      // Reset edit state when dialog closes
      setEditingContact(null);
      setAddingNew(false);
      setSearchQuery(''); // Reset search
    }
  }, [open, initialEditingContact]);

  const handleSelectContact = contact => {
    // Select the contact/address directly - no form needed
    // Address records are used as-is with their business_name (customer/site group name)
    setSelectedContactId(contact.id);
    onSelect(contact);
    onClose();
  };

  const handleEdit = (contact, event) => {
    event.stopPropagation(); // Prevent radio selection
    setEditingContact(contact);
    setAddingNew(false);
  };

  const handleAddNewClick = () => {
    setAddingNew(true);
    // Always start with a blank form for "Add Contact"
    setEditingContact(null);
  };

  const handleContactUpdated = updatedContact => {
    // Contact was saved/updated via ContactForm - select it and close
    setEditingContact(null);
    refetchContacts(); // Refresh the contact list
    handleSelectContact(updatedContact);
  };

  const handleConfirmModification = applyToRmaOnly => {
    if (applyToRmaOnly) {
      // Apply only to this RMA - use modified contact without saving
      handleSelectContact(modifiedContact);
    } else {
      // Update saved contact - call parent's onEdit handler
      if (onEdit) {
        onEdit(modifiedContact);
      }
      handleSelectContact(modifiedContact);
    }
    setShowModificationConfirm(false);
    setModifiedContact(null);
  };

  const handleCancelModification = () => {
    setShowModificationConfirm(false);
    setModifiedContact(null);
  };

  const handleCancelEdit = () => {
    setEditingContact(null);
  };

  const handleContactFormSuccess = newContact => {
    // Contact was created/used via ContactForm - select it and close
    // This handles both "Save as New" and "Use This Address" (useOnce) flows
    setAddingNew(false);
    refetchContacts(); // Refresh the contact list
    handleSelectContact(newContact);
  };

  const handleCancelAddNew = () => {
    setAddingNew(false);
    setEditingContact(null); // Clear any pre-filled contact data
  };

  const renderContactOption = contact => {
    const isSelected = selectedContactId === contact.id;
    const isAddress = contact._isAddress; // Check if Address record

    return (
      <Box
        key={contact.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 1.5,
          px: 2,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          borderRadius: 1,
        }}
      >
        {/* Radio Button */}
        <FormControlLabel value={contact.id} control={<Radio />} label="" sx={{ margin: 0, minWidth: 'auto' }} />

        {/* Contact Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Name and Badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 500 }}>
              {/* Address uses business_name, Contact uses name */}
              {isAddress ? contact.business_name : contact.name}
              {!isAddress && contact.business_name && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  - {contact.business_name}
                </Typography>
              )}
            </Typography>

            {/* Show scope badge for Address records (Customer vs Site Group) */}
            {isAddress && contact._addressScope && (
              <Chip
                label={contact._addressScope}
                size="small"
                color={contact._addressScope === 'Customer' ? 'primary' : 'secondary'}
                variant="outlined"
              />
            )}
          </Box>

          {/* Email and Phone - Skip for Address (they're empty) */}
          {!isAddress && (
            <Typography variant="body2" color="text.secondary">
              {contact.email}
              {contact.phone1 && ` | ${formatPhoneDisplay(contact.phone1)}`}
            </Typography>
          )}

          {/* Address */}
          {contact.full_address_display &&
            contact.full_address_display.trim() &&
            !contact.full_address_display.includes('None') && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {contact.full_address_display}
              </Typography>
            )}
        </Box>

        {/* Actions - Hide for Address records */}
        {!isAddress && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Primary Status Badges */}
            {(() => {
              const hasCompleteName = contact.name || contact.business_name;
              const hasCompleteAddress = contact.address && contact.city && contact.state && contact.zipcode;
              // RMA primaries require address, Sales primaries don't
              const canBeRmaPrimary = hasCompleteName && hasCompleteAddress;
              const canBeSalesPrimary = hasCompleteName;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mr: 1, alignItems: 'flex-end' }}>
                  {canBeRmaPrimary && contact.is_primary_rma_billing && (
                    <Chip
                      label={t('contacts.primary.rmaBilling', 'RMA Billing')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'var(--status-warning)',
                        color: 'white',
                      }}
                    />
                  )}
                  {canBeRmaPrimary && contact.is_primary_rma_shipping && (
                    <Chip
                      label={t('contacts.primary.rmaShipping', 'RMA Shipping')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'var(--status-warning)',
                        color: 'white',
                      }}
                    />
                  )}
                  {canBeSalesPrimary && contact.is_primary_sales_billing && (
                    <Chip
                      label={t('contacts.primary.salesBilling', 'Sales Billing')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'var(--status-ok)',
                        color: 'white',
                      }}
                    />
                  )}
                  {canBeSalesPrimary && contact.is_primary_sales_shipping && (
                    <Chip
                      label={t('contacts.primary.salesShipping', 'Sales Shipping')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'var(--status-ok)',
                        color: 'white',
                      }}
                    />
                  )}
                </Box>
              );
            })()}

            {/* Edit Button */}
            <IconButton size="small" onClick={e => handleEdit(contact, e)} title={t('contacts.editContact')}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isAddressOnlyMode
              ? t('contacts.selectAddress', 'Select Address')
              : t('contacts.addressBook', 'Address Book')}
          </Typography>
          {/* Add/Enter Contact/Address button - opens ContactForm */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddNewClick} size="small" type="button">
              {isAddressOnlyMode ? t('contacts.addAddress', 'Add Address') : t('contacts.addContact', 'Add Contact')}
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('contacts.failedToLoadContacts')}
          </Alert>
        )}

        {!loading && !error && !addingNew && (
          <>
            <RadioGroup
              value={selectedContactId}
              onChange={e => {
                // Search in all contact sources including filtered default addresses
                const contact =
                  filteredAddresses.find(c => c.id === e.target.value) ||
                  siteGroupContacts.find(c => c.id === parseInt(e.target.value)) ||
                  customerContacts.find(c => c.id === parseInt(e.target.value));
                if (contact) handleSelectContact(contact);
              }}
            >
              {/* Search Input - At top, above all contacts */}
              <TextField
                fullWidth
                size="small"
                placeholder={
                  isAddressOnlyMode
                    ? t('contacts.searchAddresses', 'Search addresses...')
                    : t('contacts.searchContacts', 'Search contacts...')
                }
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Default Addresses Section - Show at top when showDefaultAddresses is true */}
              {shouldFetchAddresses &&
                (() => {
                  // Filter out addresses without a valid address (filteredAddresses already has requiredFields filters applied)
                  const validAddresses = filteredAddresses.filter(addr => addr.address || addr.city);
                  if (validAddresses.length === 0) return null;

                  // Separate by scope
                  const siteGroupAddresses = validAddresses.filter(addr => addr._addressScope === 'Site Group');
                  const customerAddresses = validAddresses.filter(addr => addr._addressScope === 'Customer');

                  // Show addresses directly at the top - badges distinguish Site Group vs Customer
                  return (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, px: 2 }}>
                        {t('contacts.defaultAddresses', 'Default Addresses')}
                      </Typography>
                      {siteGroupAddresses.map(renderContactOption)}
                      {customerAddresses.map(renderContactOption)}
                    </>
                  );
                })()}

              {/* Site Group Contacts Section - Hide in addressOnly mode */}
              {!isAddressOnlyMode && siteGroupContacts.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, px: 2 }}>
                    {t('contacts.siteGroupContacts', 'Site Group Contacts')}
                  </Typography>
                  {siteGroupContacts.map(renderContactOption)}
                </>
              )}

              {/* Customer Contacts Section - Hide in addressOnly mode */}
              {!isAddressOnlyMode && customerContacts.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, px: 2 }}>
                    {t('contacts.customerContacts', 'Customer Contacts')}
                  </Typography>
                  {customerContacts.map(renderContactOption)}
                </>
              )}

              {/* No Contacts / No Results - Different message for addressOnly mode */}
              {!isAddressOnlyMode && siteGroupContacts.length === 0 && customerContacts.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? t('contacts.noSearchResults', 'No contacts match your search')
                      : t('contacts.noContactsAvailable')}
                  </Typography>
                  {!searchQuery && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={handleAddNewClick}
                      sx={{ mt: 2 }}
                      type="button"
                    >
                      {t('contacts.addFirstContact')}
                    </Button>
                  )}
                </Box>
              )}

              {/* No Addresses in addressOnly mode */}
              {isAddressOnlyMode && filteredAddresses.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('contacts.noAddressesAvailable', 'No default addresses available')}
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={handleAddNewClick}
                    sx={{ mt: 2 }}
                    type="button"
                  >
                    {t('contacts.addNewAddress', 'Add New Address')}
                  </Button>
                </Box>
              )}
            </RadioGroup>
          </>
        )}
      </DialogContent>

      {/* Contact Modification Confirmation Dialog */}
      <ContactModificationConfirmDialog
        open={showModificationConfirm}
        onClose={handleCancelModification}
        onConfirm={handleConfirmModification}
        modifiedContact={modifiedContact}
      />

      {/* Add New / Edit Contact Dialog - hasExistingContact/useOnceOnly now derived from contact prop */}
      <ContactForm
        open={addingNew || !!editingContact}
        onClose={addingNew ? handleCancelAddNew : handleCancelEdit}
        contact={editingContact}
        customerId={customerId}
        siteGroupId={siteGroupId}
        onSuccess={addingNew ? handleContactFormSuccess : handleContactUpdated}
        mode="dialog"
        isManualEntry={isAddressOnlyMode}
        standaloneMode={true}
        showBackButton={true}
        onBackToAddressBook={() => {
          setAddingNew(false);
          setEditingContact(null);
        }}
        allowUseOnce={!savedContactsOnly}
        savedContactsOnly={savedContactsOnly}
        requiredFields={requiredFields}
        primaryContext={isAddressOnlyMode ? 'none' : primaryContext}
        addressOnlyMode={isAddressOnlyMode}
        customerName={customerDisplayName}
        siteGroupName={siteGroupDisplayName}
      />
    </Dialog>
  );
};

export default AddressBookDialog;
